<script setup lang="ts" generic="T">
import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import SearchBox from '@/components/SearchBox.vue'
import { chosenLevels, drillDown, filterBySearch, type DrillLevel } from '@/settings/drillDown'
import { showBackButton } from '@/telegramWebApp'
import UiRow from '@/components/UiRow.vue'

// A long list as a hierarchy instead of a wide table: a search box, the groups of the next level as rows (name, count, ›),
// and at the end the items themselves in the `item` slot, one narrow card each. A search lists the matching items at
// once, whatever the level. The chosen path is `?<queryKey>.drill=a/b` (each part encoded), the search `?<queryKey>.q=`,
// so F5, a link and Back keep the place; with Telegram's back button it steps one level up.
const props = withDefaults(
  defineProps<{
    items: readonly T[]
    levels: readonly DrillLevel<T>[]
    texts: (item: T) => string[] // (what the search looks at)
    queryKey: string
    placeholder: string
    local?: boolean // (false: the page filters by the search itself, e.g. on the server)
    itemKey: (item: T) => string | number
  }>(),
  { local: true },
)
const emit = defineEmits<{ search: [value: string] }>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const drillKey = computed(() => `${props.queryKey}.drill`)
const searchKey = computed(() => `${props.queryKey}.q`)

function queryString(key: string): string {
  const value = route.query[key]
  return typeof value === 'string' ? value : ''
}

const path = computed(() =>
  queryString(drillKey.value) ? queryString(drillKey.value).split('/').map(decodeURIComponent) : [],
)
const search = ref(queryString(searchKey.value))

function writeQuery(changes: Record<string, string>, replace: boolean): void {
  const query = { ...route.query }
  for (const [key, value] of Object.entries(changes)) {
    if (value) query[key] = value
    else delete query[key]
  }
  void (replace ? router.replace({ query }) : router.push({ query }))
}

function setPath(next: readonly string[]): void {
  writeQuery({ [drillKey.value]: next.map(encodeURIComponent).join('/') }, false)
}

let timer: ReturnType<typeof setTimeout> | undefined
watch(search, (value) => {
  clearTimeout(timer)
  timer = setTimeout(() => {
    writeQuery({ [searchKey.value]: value.trim() }, true)
    emit('search', value.trim())
  }, 300)
})
onBeforeUnmount(() => clearTimeout(timer))
// (a link or Back that changes the search in the URL)
watch(
  () => queryString(searchKey.value),
  (value) => {
    if (value !== search.value.trim()) search.value = value
  },
)

const searching = computed(() => props.local && search.value.trim() !== '')
const step = computed(() =>
  searching.value
    ? ({ kind: 'items', items: filterBySearch(props.items, search.value, props.texts) } as const)
    : drillDown(props.items, props.levels, path.value),
)
const chosen = computed(() => (searching.value ? [] : chosenLevels(props.items, props.levels, path.value)))
const shownItems = computed(() => (step.value.kind === 'items' ? step.value.items : []))
const shownGroups = computed(() => (step.value.kind === 'groups' ? step.value.groups : []))

// (Telegram's back button takes the topmost handler, so while a level is chosen it goes up before the page's own)
watchEffect((onCleanup) => {
  if (path.value.length === 0 || searching.value) return
  onCleanup(showBackButton(() => setPath(path.value.slice(0, -1))))
})
</script>

<template>
  <div class="drill" data-testid="drill">
    <SearchBox v-model="search" :placeholder="placeholder" :clear-label="t('common.clear')" />

    <nav v-if="!searching && path.length > 0" class="crumbs" :aria-label="t('drill.path')">
      <button type="button" class="crumb" @click="setPath([])">{{ t('drill.all') }}</button>
      <template v-for="(part, index) in path" :key="index">
        <span aria-hidden="true">›</span>
        <button type="button" class="crumb" @click="setPath(path.slice(0, index + 1))">
          {{ part }}
        </button>
      </template>
    </nav>

    <template v-if="step.kind === 'groups'">
      <UiRow
        as="button"
        v-for="group in shownGroups"
        :key="group.value"
        type="button"
        class="group"
        data-testid="drill-group"
        @click="setPath([...path, group.value])"
      >
        <span class="name">{{ group.value }}</span>
        <span class="count">{{ t('drill.count', group.count) }}</span>
        <span class="go" aria-hidden="true">›</span>
      </UiRow>
    </template>
    <template v-else>
      <p v-if="shownItems.length === 0" class="ui-hint">{{ t('drill.noMatch') }}</p>
      <div
        v-for="item in shownItems"
        :key="itemKey(item)"
        class="drill-card"
        data-testid="drill-item"
      >
        <slot name="item" :item="item" :chosen="chosen" />
      </div>
    </template>
  </div>
</template>

<style scoped>
.drill {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.crumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--tg-hint-color);
}

.crumb {
  background: none;
  border: 0;
  padding: 0.15rem 0.25rem;
  color: var(--tg-link-color);
  font: inherit;
  overflow-wrap: anywhere;
  text-align: left;
}

.group {
  width: 100%;
  border: 0;
  border-radius: 12px;
  background: var(--tg-section-bg-color);
  color: inherit;
  font: inherit;
  text-align: left;
  gap: 0.5rem;
}

.name {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.count,
.go {
  color: var(--tg-hint-color);
  flex: none;
}
</style>

<style>
/* (the card of a leaf item; global because the slot content belongs to the page) */
.drill-card {
  padding: 0.25rem 0.85rem 0.6rem;
  border-radius: 12px;
  background: var(--tg-section-bg-color);
  min-width: 0;
}

.drill-card .field {
  padding: 0.45rem 0;
  min-width: 0;
}

.drill-card .field + .field {
  border-top: 1px solid var(--tg-secondary-bg-color);
}

.drill-card dl,
.drill-card dd {
  margin: 0;
  padding: 0;
}

.drill-card dt {
  font-size: 0.8rem;
  color: var(--tg-hint-color);
}

.drill-card dd {
  overflow-wrap: anywhere;
}
</style>
